<template>
    <div>
        <div class="form-group form-row">
            <label for="type" class="col-md-4 col-form-label">Type:</label>
            <div class="col-md-8 col-form-label">
                Medical
            </div>
        </div>
        <div class="form-group form-row">
            <label for="medication" class="col-md-4 col-form-label"
                >Medication:</label
            >
            <div class="col-md-8 col-form-label">
                {{ item.display_name }}
            </div>
        </div>
        <div class="form-group form-row">
            <label for="quantity" class="col-md-4 col-form-label"
                >Quantity:</label
            >
            <div class="col-md-8">
                <div class="form-row">
                    <div class="col-md-9">
                        <input
                            type="number"
                            class="form-control"
                            id="quantity"
                            name="quantity"
                            v-model="form.quantity"
                            placeholder="Quantity (required)"
                            v-validate="
                                'required|min_value:0|numericalWithDecimal|decimal:3'
                            "
                            :class="{
                                'border-danger': errors.has('quantity')
                            }"
                        />
                    </div>
                    <div class="col-md-3 d-flex align-items-center">
                        <span v-if="item">{{
                            item.uom ? item.uom : "UOM"
                        }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="form-group form-row">
            <label for="route" class="col-md-4 col-form-label">Route:</label>
            <div class="col-md-8">
                <select v-model="form.routeId" class="form-control" id="route">
                    <option
                        v-for="option in routeOptions"
                        :key="option.value"
                        :value="option.value"
                        >{{ option.text }}
                    </option>
                </select>
            </div>
        </div>

        <div class="form-group form-row">
            <label for="location" class="col-md-4 col-form-label"
                >Location:</label
            >
            <div class="col-md-8">
                <select
                    v-model="form.locationId"
                    class="form-control"
                    id="route"
                >
                    <option
                        v-for="option in locationOptions"
                        :key="option.value"
                        :value="option.value"
                        >{{ option.text }}</option
                    >
                </select>
            </div>
        </div>

        <div class="form-group form-row">
            <label for="due" class="col-md-4 col-form-label">Due:</label>
            <div class="col-md-8">
                <div class="d-flex align-items-center">
                    <select
                        v-model="form.due.type"
                        class="form-control"
                        id="due"
                    >
                        <option
                            v-for="type in activityTypes"
                            :key="type.type"
                            :value="type.type"
                            >{{ type.text }}
                        </option>
                    </select>

                    <input
                        v-if="isActivityDueAfterStartTreatment"
                        v-model="form.due.afterStartTime"
                        id="after-start-time"
                        name="after_start_time"
                        type="number"
                        v-validate="'required|numeric'"
                        :class="{
                            'border-danger': errors.has('after_start_time')
                        }"
                        min="1"
                        max="99"
                        step="1"
                        class="form-control ml-1 number-width"
                    />
                    <timepicker
                        v-if="isActivityDueAtSpecificTime"
                        v-model="form.due.specificTime"
                        id="specific-time"
                        name="specific_time"
                        v-validate="'required'"
                        :class="{
                            'border-danger': errors.has('specific_time')
                        }"
                        class="form-control ml-1"
                        style="width: 8.8rem;"
                    />

                    <span v-if="isActivityDueAfterStartTreatment" class="ml-1">
                        minutes(s)
                    </span>
                </div>
            </div>
        </div>
        <div class="form-group form-row">
            <label for="repeat" class="col-md-4 col-form-label">Repeat:</label>
            <div class="col-md-8">
                <select
                    v-model="form.isRepeat"
                    class="form-control"
                    id="repeat"
                    name="repeat"
                >
                    <option value="0">Do not repeat</option>
                    <option value="1">Repeat</option>
                </select>
                <div
                    v-if="isActivityRepeat"
                    class="d-flex align-items-center mt-2"
                >
                    <input
                        type="number"
                        id="occurence"
                        name="occurence"
                        v-model="form.repeat.occurence"
                        v-validate="'required|numeric'"
                        :class="{
                            'border-danger': errors.has('occurence')
                        }"
                        min="1"
                        max="99"
                        step="1"
                        class="form-control number-width mr-1"
                    />
                    <span class="mr-1 text-nowrap">
                        times every
                    </span>
                    <input
                        type="number"
                        id="specific-interval"
                        name="specific_interval"
                        v-model="form.repeat.specificInterval"
                        v-validate="'required|numeric'"
                        :class="{
                            'border-danger': errors.has('specific_interval')
                        }"
                        min="1"
                        max="99"
                        step="1"
                        class="form-control number-width mr-1"
                    />
                    <select
                        id="repeat-interval"
                        name="repeat_interval"
                        class="form-control"
                        v-model="form.repeat.intervalType"
                    >
                        <option
                            v-for="option in repeatIntervalOptions"
                            :key="option.type"
                            :value="option.type"
                            >{{ option.text }}</option
                        >
                    </select>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { DateTime } from "luxon";
import ItemServices from "@/services/items.js";
import Timepicker from "@/module/commons/time-picker.vue";

export default {
    components: {
        Timepicker
    },
    props: {
        item: {
            type: Object,
            required: true
        },
        formData: {
            type: Object
        }
    },
    data() {
        return {
            locations: [],
            routes: [],
            loading: {
                route: false,
                location: false
            },
            form: {
                quantity: 0,
                routeId: 0,
                locationId: 0,
                isRepeat: 0,
                due: {
                    type: 1,
                    afterStartTime: 15,
                    specificTime: null
                },
                repeat: {
                    type: 1,
                    occurence: 1,
                    intervalType: "minutes",
                    specificInterval: 15,
                    specificTime: null
                },
                ...this.formData
            }
        };
    },
    mounted() {
        this.getRoutes();
        this.getLocations();
        this.setDefaultField();
    },
    watch: {
        "form.due.type"(value) {
            if (this.isActivityDueAtSpecificTime) {
                this.form.due.specificTime = DateTime.local().setZone(
                    this._timeZone
                );
                this.form.due.afterStartTime = null;
            } else {
                this.form.due.afterStartTime = 15;
                this.form.due.specificTime = null;
            }
        },
        isSaveDisabled(value) {
            this.$emit("is-save-disabled", value);
        }
    },
    computed: {
        templateId() {
            return this.$store.state.settingsTemplate.details.id;
        },
        isActivityDueAfterStartTreatment() {
            return this.form.due.type === 1;
        },
        isActivityDueAtSpecificTime() {
            return this.form.due.type === 2;
        },
        isActivityRepeat() {
            return this.form.isRepeat === "1";
        },
        isSaveDisabled() {
            return this.loading.route || this.loading.location;
        },
        activityTypes() {
            return [
                {
                    type: 1,
                    text: "After start of treatment"
                },
                {
                    type: 2,
                    text: "At specific time"
                }
            ];
        },
        repeatIntervalOptions() {
            return [
                {
                    type: "minutes",
                    text: "Minute(s)"
                },
                {
                    type: "hours",
                    text: "Hour(s)"
                },
                {
                    type: "days",
                    text: "Day(s)"
                },
                {
                    type: "weeks",
                    text: "Week(s)"
                },
                {
                    type: "months",
                    text: "Month(s)"
                }
            ];
        },
        routeOptions() {
            return [
                { value: "0", text: "None" },
                ...this.routes.map(route => ({
                    value: route.id,
                    text: route.value
                }))
            ];
        },
        locationOptions() {
            return [
                { value: "0", text: "None" },
                ...this.locations.map(location => ({
                    value: location.id,
                    text: location.value
                }))
            ];
        }
    },
    methods: {
        reset() {
            this.form.item = null;
            this.form.quantity = 1;
            this.form.routeId = null;
            this.form.locationId = null;
            this.form.isRepeat = 0;
            this.form.isPrintOnInvoice = 1;
            this.form.due.type = 1;
            this.form.due.afterStartTime = 15;
            this.form.due.specificTime = null;
            this.form.repeat.type = 1;
            this.form.repeat.occurence = 1;
            this.form.repeat.intervalType = "days";
            this.form.repeat.specificInterval = 15;

            this.$validator.reset();
        },
        setDefaultField() {
            this.form.routeId = this.item.details.vaccinationRoute.id || 0;
            this.form.locationId =
                this.item.details.vaccinationLocation.id || 0;
        },
        getRoutes() {
            this.loading.route = true;

            ItemServices.getRouteOptions().then(data => {
                this.routes = data;
                this.loading.route = false;
            });
        },
        getLocations() {
            this.loading.location = true;

            ItemServices.getLocationOptions().then(data => {
                this.locations = data;
                this.loading.location = false;
            });
        },
        async save() {
            let res = {};

            try {
                this.isLoading = true;

                const payload = {
                    template_id: this.templateId,
                    activity_type: 1, // medical
                    activity: {
                        item_id: this.item.id,
                        quantity: this.form.quantity || 1,
                        route_id: this.form.routeId,
                        location_id: this.form.locationId,
                        due: {
                            type: this.form.due.type,
                            after_start_time: this.form.due.afterStartTime,
                            specific_time: this.isActivityDueAtSpecificTime
                                ? this.form.due.specificTime.toFormat("hh:mm a")
                                : this.form.due.specificTime
                        }
                    }
                };

                if (this.isActivityRepeat && this.form.repeat.occurence > 1) {
                    payload.activity.repeat = {
                        type: this.form.repeat.type,
                        occurence: this.form.repeat.occurence,
                        specific_interval: this.convertToMinute(
                            this.form.repeat.specificInterval,
                            this.form.repeat.intervalType
                        ),
                        specific_time: this.form.repeat.specificTime
                    };
                }

                res = await this.$http.post(
                    `${process.env.BARRAMUNDI}schedule/treatment-template-activity`,
                    payload
                );

                if (
                    res.data.response &&
                    res.data.response.message &&
                    res.data.response.message.code != 200
                ) {
                    throw new Error(res.data.response.message);
                }

                this._message("Activity created");
            } catch (error) {
                console.log(error.message);
                this._message("Error saving data", "danger");
            } finally {
                this.isLoading = false;
            }
        },
        convertToMinute(interval, fromInterval) {
            let inMinute = "";
            switch (fromInterval) {
                case "minutes":
                    inMinute = interval;
                    break;
                case "hours":
                    inMinute = interval * 60;
                    break;
                case "days":
                    inMinute = interval * 24 * 60;
                    break;
                case "weeks":
                    inMinute = interval * 7 * 24 * 60;
                    break;
                case "months":
                    inMinute = interval * 30 * 24 * 60;
                    break;
            }
            return inMinute;
        }
    }
};
</script>

<style scoped>
.number-width {
    width: 3rem;
}
</style>
